import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { getOnEnter, getUniqueKey } from '../../_utils';
import { Avatar } from '../../avatar';
import { Button } from '../../button';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { Menu } from '../../menu';
import { Popover } from '../../popover';
import styles from './chip-base.module.scss';
import { getChipContent, renderCloseIcon } from './chip-base-utils';
export const ChipBase = ({ metaIconConfig, content, id, className, containerClassName, icon, avatarConfig, onClick, onClose, type = 'default', size = 'm', disabled = false, nonInteractive = false, selected = false, fullWidth = false, options = [], variant = 'select', edit = false, onEditAcive, onEditDone, onChange, onIconClick, hasCompactContent, closeIconTooltipContent = '', disabledTooltipContent = '', chipState = 'default', iconAriaLabel, ...restProps }) => {
    const targetRef = useRef(null);
    const inputRef = useRef(null);
    const [chipContent, setChipContent] = useState(content);
    const [isEdited, setIsEdited] = useState(edit);
    const [isOptionsVisible, setIsOptionsVisible] = useState(false);
    const handleClose = (event) => {
        event.stopPropagation();
        if (onClose) {
            onClose();
        }
    };
    const handleIconClick = (event) => {
        if (onIconClick) {
            event.stopPropagation();
            onIconClick();
        }
    };
    const handleDocumentClick = (event) => {
        var _a;
        const isInput = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
        setIsEdited(isInput);
    };
    useEffect(() => {
        setChipContent(content);
    }, [content]);
    useEffect(() => {
        setIsEdited(edit);
    }, [edit]);
    useEffect(() => {
        if (onEditDone && !isEdited) {
            onEditDone(chipContent[0].text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdited]);
    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, []);
    const chipWrapperClassnames = cx(styles.chip, styles[type], styles[variant], styles[chipState], className, size === 's' ? styles.sizeSmall : styles.sizeMedium, {
        [styles.edit]: isEdited,
        [styles.disabled]: disabled,
        [styles.nonInteractive]: nonInteractive,
        [styles.fullWidth]: fullWidth,
        [styles.selected]: selected,
    });
    const splitIconClassNames = cx(styles.splitIcon, {
        [styles.open]: isOptionsVisible,
    });
    const getOptionsContent = () => {
        return (React.createElement(Menu, { show: true }, options.map((option) => {
            return (React.createElement(Menu.Item, { ...option, key: getUniqueKey(option), onSelect: (item) => {
                    setIsOptionsVisible(false);
                    if (option.onSelect) {
                        option.onSelect(item);
                    }
                } }));
        })));
    };
    const handleClick = (e) => {
        if (variant === 'search') {
            setIsEdited(true);
            if (onEditAcive) {
                onEditAcive();
            }
        }
        if (onClick) {
            onClick(e);
        }
    };
    const handleTextChange = (event) => {
        setChipContent([
            {
                ...chipContent[0],
                text: event.target.value,
            },
        ]);
        if (onChange) {
            onChange(event);
        }
    };
    const renderEditText = () => {
        const { text = '' } = chipContent[0];
        return (React.createElement("span", { className: styles.content, ref: inputRef },
            React.createElement("input", { type: "text", className: styles.contentInput, spellCheck: false, 
                // eslint-disable-next-line
                autoFocus: true, value: text, onChange: handleTextChange, style: { width: `${(text.length + 1) * 8}px` } })));
    };
    const textContentClassNames = cx(styles.content, {
        [styles.noAvatar]: variant === 'input' &&
            (!avatarConfig ||
                !Object.prototype.hasOwnProperty.call(avatarConfig, 'name')),
    }, { [styles.defaultContent]: !hasCompactContent });
    return (React.createElement("div", { className: cx(size === 's' ? styles.sizeSmall : styles.sizeMedium, {
            [styles.splitTriggerWrapper]: options.length,
        }, containerClassName), "data-tooltip-content": disabledTooltipContent },
        React.createElement("div", { className: chipWrapperClassnames, onClick: handleClick, id: id, "data-testid": id, tabIndex: nonInteractive ? -1 : 0, role: "button", onKeyDown: getOnEnter(onClick), ...restProps },
            metaIconConfig && !avatarConfig && (React.createElement("div", { className: styles.metaIcon, "data-tooltip-content": metaIconConfig.tooltip || '' },
                React.createElement(Icon, { id: metaIconConfig.id, "data-testid": "chip-base-meta-icon", color: metaIconConfig.color || IconColor.RED, size: metaIconConfig.size || IconSize.XLARGE }))),
            avatarConfig &&
                Object.prototype.hasOwnProperty.call(avatarConfig, 'name') && (React.createElement(Avatar, { size: "s", showName: false, className: styles.avatar, name: avatarConfig.name, src: avatarConfig.src })),
            selected && (React.createElement(React.Fragment, null,
                variant === 'input' && (React.createElement("div", { className: styles.selectedOverlay })),
                React.createElement("span", { className: styles.tickIcon },
                    React.createElement(Icon, { color: IconColor.WHITE, size: IconSize.SMALL, id: IconID.CHECKMARK })))),
            isEdited
                ? renderEditText()
                : getChipContent(variant, chipContent, textContentClassNames, hasCompactContent),
            icon && !options.length && (React.createElement(Button, { icon: icon, onClick: handleIconClick, onKeyDown: getOnEnter(handleIconClick), className: styles.chipIcon, size: "s", type: "tertiary", tabIndex: onIconClick ? 0 : -1, ariaLabel: iconAriaLabel })),
            onClose &&
                !isEdited &&
                renderCloseIcon(variant, closeIconTooltipContent, handleClose)),
        !!options.length && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: splitIconClassNames, ref: targetRef, onClick: () => setIsOptionsVisible(!isOptionsVisible), onKeyDown: () => setIsOptionsVisible(!isOptionsVisible), role: "button", tabIndex: 0 },
                React.createElement(Icon, { color: IconColor.TEXT_COLOR, size: IconSize.SMALL, id: IconID.CHEVRON_DOWN })),
            React.createElement(Popover, { targetRef: targetRef, content: getOptionsContent(), isVisible: isOptionsVisible, align: "bottom-start", appendToBody: true, onClickOutside: () => {
                    setIsOptionsVisible(false);
                } })))));
};
//# sourceMappingURL=chip-base.js.map